import { FormBuilder } from "components/FormBuilder/FormBuilder";
import React from 'react'
import { useTranslation } from "react-i18next";
import { OnlineMerchantManagement } from "../OnlineMerchantManagement";
import dayjs from "dayjs";
import ShareholderFields from "pages/merchant-management/merchants/offline-merchant-detail/Component/ShareholderFields";
import Divider from "components/Divider/Divider";

/**
 * @param {Object} props 
 * @param {import("react-hook-form").UseFormReturn} props.formProps
 * @param {import("components/FormBuilder/lib/FormBuilderFieldBuilder").formBuilderSelectionOptionsType[]} props.mccDropdownOptions
 * @param {boolean} props.isCreate
 * @param {boolean} props.isEditing
 * @param {boolean} props.isLoading
 */
const OnlineMerchantManagementBasicInfoSection = ({
  formProps,
  mccDropdownOptions,
  isCreate,
  isEditing,
  isLoading
}) => {
  const { t: tLocal } = useTranslation("translation", { keyPrefix: "form.onlineMerchantManagement.merchant.basicInfoSection" });
  const { t: tCommon } = useTranslation();

  const isDisabled = isLoading;
  const isReadOnly = !isCreate && !isEditing;

  /** @type {Record<string, import("components/FormBuilder/lib/FormBuilderFieldBuilder").formBuilderFieldConfig>} */
  const fieldsConfig = {
    displayName: {
      fieldType: "textInput",
      fieldName: "displayName",
      label: tLocal("label.displayName"),
      disabled: isDisabled,
      readOnly: isReadOnly,
    },
    name: {
      fieldType: "textInput",
      fieldName: "name",
      label: tLocal("label.name"),
      mandatory: true,
      disabled: isDisabled,
      readOnly: isReadOnly,
      config: {
        minLength: 2,
        maxLength: 25,
      }
    },
    countryOrRegion: {
      fieldType: "selection",
      fieldName: "countryOrRegion",
      label: tLocal("label.countryOrRegion"),
      mandatory: true,
      disabled: isDisabled,
      readOnly: isReadOnly,
      config: {
        options: OnlineMerchantManagement.Options.CountryOrRegion,
        defaultValue: "HK",
      }
    },
    legalName: {
      fieldType: "textInput",
      fieldName: "legalName",
      label: tLocal("label.legalName"),
      mandatory: true,
      disabled: isDisabled,
      readOnly: isReadOnly,
      config: {
        maxLength: 50,
        rules: [
          "noLeadingWhitespace",
          "noQuotesOrBackticks",
          "onlyAlphabetNumChineseAndSpaces",
        ],
      }
    },
    businessRegistration: {
      fieldType: "textInput",
      fieldName: "businessRegistration",
      label: tLocal("label.businessRegistration"),
      mandatory: true,
      disabled: isDisabled,
      readOnly: isReadOnly,
      config: {
        rules: [
          "validBusinessRegNo",
        ],
      }
    },
    businessEstablishedDate: {
      fieldType: "datePicker",
      fieldName: "businessEstablishedDate",
      label: tLocal("label.businessEstablishedDate"),
      mandatory: true,
      disabled: isDisabled,
      readOnly: isReadOnly,
      config: {
        defaultValue: new Date(),
        maxDate: dayjs(),
        rules: [
          "validDate",
          "onlyBeforeToday"
        ]
      }
    },
    address: {
      fieldType: "textInput",
      fieldName: "address",
      label: tLocal("label.address"),
      mandatory: true,
      disabled: isDisabled,
      readOnly: isReadOnly,
      config: {
        rules: [
          "firstCharLetterOrNumber",
        ]
      }
    },
    addressSecondaryLang: {
      fieldType: "textInput",
      fieldName: "addressSecondaryLang",
      label: tLocal("label.addressSecondaryLang"),
      disabled: isDisabled,
      readOnly: isReadOnly,
    },
    city: {
      fieldType: "selection",
      fieldName: "city",
      label: tLocal("label.city"),
      mandatory: true,
      disabled: isDisabled,
      readOnly: isReadOnly,
      config: {
        options: OnlineMerchantManagement.Options.City,
      }
    },
    stateOrProvince: {
      fieldType: "textInput",
      fieldName: "stateOrProvince",
      label: tLocal("label.stateOrProvince"),
      mandatory: true,
      disabled: isDisabled,
      readOnly: isReadOnly,
      config: {
        defaultValue: "Hong Kong",
      }
    },
    contactPerson: {
      fieldType: "textInput",
      fieldName: "contactPerson",
      label: tLocal("label.contactPerson"),
      mandatory: true,
      disabled: isDisabled,
      readOnly: isReadOnly,
      config: {
        rules: [
          "noLeadingWhitespace",
          "noQuotesOrBackticks",
          "noChineseCharacters",
          "noFullwidthCharacters",
          "noSpecialSpaces",
        ]
      }
    },

    email: {
      fieldType: "textInput",
      fieldName: "email",
      label: tLocal("label.email"),
      mandatory: true,
      disabled: isDisabled,
      readOnly: isReadOnly,
      config: {
        rules: [
          "validEmail",
        ]
      }
    },
    phone: { //TOOD add validation
      fieldType: "textInput",
      fieldName: "phone",
      label: tLocal("label.phone"),
      mandatory: true,
      disabled: isDisabled,
      readOnly: isReadOnly,
      config: {
        rules: [
          "validOnlinePhone",
        ]
      }
    },
    mcc: {
      fieldType: "selection",
      fieldName: "mcc",
      label: tLocal("label.mcc"),
      mandatory: true,
      disabled: isDisabled,
      readOnly: isReadOnly,
      config: {
        options: mccDropdownOptions
      }
    },
  }

  return (
    <FormBuilder.Section
      title={tLocal("title")}
      titleVariant="h3"
    >
      <FormBuilder.Row itemsPerRow={2}>
        {
          FormBuilder.buildFields({
            formProps,
            t: tCommon,
            fields: [
              fieldsConfig.displayName,
              fieldsConfig.name,
              fieldsConfig.countryOrRegion,
              fieldsConfig.legalName,
              fieldsConfig.businessRegistration,
              fieldsConfig.businessEstablishedDate,
              fieldsConfig.address,
              fieldsConfig.addressSecondaryLang,
              fieldsConfig.city,
              fieldsConfig.stateOrProvince,
              fieldsConfig.contactPerson,
            ]
          })
        }
      </FormBuilder.Row>
      <FormBuilder.Row itemsPerRow={2}>
        {
          FormBuilder.buildFields({
            formProps,
            t: tCommon,
            fields: [
              fieldsConfig.email,
              fieldsConfig.phone,
              fieldsConfig.mcc,
            ]
          })
        }
      </FormBuilder.Row>
      <Divider />
      <ShareholderFields
        formProps={formProps}
        disabled={isDisabled}
        readOnly={isReadOnly}
      />
    </FormBuilder.Section>
  )
}

export default OnlineMerchantManagementBasicInfoSection